import { isResourcePriceByTime } from "@@/shared/acquire/resource-price/resource-price-by-time";
import { isResourcePriceFixed } from "@@/shared/acquire/resource-price/resource-price-fixed";
import type { BookableOptionValue } from "@@/shared/acquire/resource-reference-options/bookable-option-value";
import { convertTimeByGranularity } from "@@/shared/date-time/time-granularity";
import { inMinutes } from "@@/shared/date-time/time-spans";
import { conditionFilter } from "@@/shared/discounts/conditions/discount-condition-filter";
import type { DiscountPrice } from "@@/shared/discounts/discount-types/time-step/time-step-discount-calculator";
import type { OrderItem_V1 } from "@@/shared/orders/order-items/order-item-v1";
import type { OrderItem_V2 } from "@@/shared/orders/order-items/order-item-v2";
import type { Price } from "@@/shared/prices/price";
import { priceFactory } from "@@/shared/prices/price";
import { sumPrices } from "@@/shared/prices/sum-prices";
import { repeat } from "@@/utilities/repeat";
import { sortBy } from "@@/utilities/sort";
import type { TimePriceStepDiscount } from "./time-price-step-discount";

const timePriceStepDiscountCalculator =
    (discount: TimePriceStepDiscount) =>
    (item: OrderItem_V1 | OrderItem_V2, values: BookableOptionValue) => {
        const entitledItems = conditionFilter(discount, [item], {
            resource: values.resourceId,
        });

        if (
            entitledItems.length !== 1 ||
            item.acquireType !== "BOOKABLE" ||
            values._type !== "QUANTITY_PICKER_BOOKABLE_OPTION_VALUE"
        )
            return undefined;

        const option = values;

        if (option?._type === "QUANTITY_PICKER_BOOKABLE_OPTION_VALUE") {
            const numberOfBookedTime = inMinutes({
                seconds: item.time.duration,
            });
            if (isResourcePriceByTime(option.resourcePrice)) {
                const stepInMinutes = convertTimeByGranularity(
                    option.resourcePrice.step,
                    option.resourcePrice.granularity,
                    "MINUTE",
                );
                const timeSteps = Math.ceil(numberOfBookedTime / stepInMinutes);

                const prices: Price[] = [];
                for (let i = 0; i < timeSteps; i++) {
                    const price = discount.steps
                        .sort(sortBy(f => f.step, "DESCENDING"))
                        .find(f => i * stepInMinutes >= f.step);

                    if (price) {
                        prices.push(
                            priceFactory({
                                amountIncludingVat: price.price
                                    ? price.price.amountIncludingVat
                                    : price.amountIncludingVat,
                                vat: option.resourcePrice.price.vat,
                            }),
                        );
                    } else {
                        prices.push(option.resourcePrice.price);
                    }
                }

                const discountPrice = sumPrices(prices);
                return {
                    pricePer: discountPrice,
                    totalPrice: sumPrices(
                        repeat(discountPrice, option.quantity.value),
                    ),
                } as DiscountPrice;
            } else if (isResourcePriceFixed(option.resourcePrice)) {
                //If fixed price check step to get the "best"
                const price = discount.steps
                    .sort(sortBy(f => f.step, "DESCENDING"))
                    .find(f => numberOfBookedTime >= f.step);

                if (price) {
                    const priceWithCorrectVat = priceFactory({
                        amountIncludingVat: price.price
                            ? price.price.amountIncludingVat
                            : price.amountIncludingVat,
                        vat: option.resourcePrice.price.vat,
                    });
                    return {
                        pricePer: priceWithCorrectVat,
                        totalPrice: sumPrices(
                            repeat(priceWithCorrectVat, option.quantity.value),
                        ),
                    } as DiscountPrice;
                }
            }
        }

        //console.log("discountPriceResource", discountPrice);
        return undefined;
    };

export { timePriceStepDiscountCalculator };
